import { Inviter, SessionState, UserAgent } from 'sip.js';
import ringtoneUrl from '../gudhub-ringtone.mp3';
import ringbackUrl from '../gudhub-ringback.mp3';
import { pruneVideoCodecs, debundleVideo } from './sdpModifiers.js';
import { HEARTBEAT_TIMEOUT } from './sipRegistrationMixin.js';
import { traceCall } from './callTrace.js';
import { endReasonFromMediaError, endReasonFromSipStatus } from '../callScreen/callScreenModel.js';

const AUDIO_CONSTRAINTS = { audio: true, video: false };
const VIDEO_CONSTRAINTS = { audio: true, video: true };

// How long the call screen waits for the callee's name before showing the
// number or id it was given - a cached user resolves well within this.
const NAME_WAIT_MS = 400;

export const callSessionMixin = {

    //********************* START OUTGOING CALL ********************************//
    // * detail ({ callee_id, video }) — dispatched by gh-call-profile's phone/camera buttons
    async startOutgoingCall({ callee_id, video = true }) {
        if(this.nativeCallView) {
            this.toast?.customToast({
                template: `<md-toast><p>Finish the current call first.</p></md-toast>`,
                hideDelay: 4000
            });
            return;
        }

        // Already placing one: a second tap, or one tap dispatched twice.
        if(this.callState === 'outgoing_ringing' && !this.session) return;

        if(this.callState !== 'registered') {
            console.warn(`Cannot start call - SIP callState is '${this.callState}', expected 'registered'`);
            this.toast?.customToast({
                template: `<md-toast><p>Still connecting - try again in a moment.</p></md-toast>`,
                hideDelay: 4000
            });
            return;
        }

        // Claimed before the first await. Two taps a moment apart - or one tap
        // dispatched twice - both passed the check above while the OPTIONS
        // probe below was in flight, and every device of the person called
        // rang for two calls at once (seen in the SIP server's log: two pushes
        // 74 ms apart for one call).
        this.callState = 'outgoing_ringing';
        // Video is a placeholder in the mobile apps for now (the call screen's
        // camera button says so): calls from there are audio calls.
        if(this.isNativeShell?.()) video = false;
        const attempt = (this._outgoingAttempt = { callee_id });
        const stillPlacing = () => this._outgoingAttempt === attempt && this.callState === 'outgoing_ringing';
        this._lastCallee = { callee_id, video };
        this._endReason = null;
        this.isMuted = false;
        this.isCameraOff = false;
        this.callVideo = false;

        // callee_id is usually a GudHub user id, but gh-call-profile's Keypad
        // tab dispatches this same event for a raw dialed PSTN number too -
        // getUserById() won't find one, so fall back to displaying the
        // number itself rather than leaving the call screen showing "Unknown".
        //
        // The screen comes up straight away - the tap is answered before the
        // network is - with the name if it is already known, and the name
        // follows a moment later otherwise.
        const lookup = Promise.resolve()
            .then(() => gudhub.getUserById(String(callee_id)))
            .catch(() => null);
        const known = await Promise.race([lookup, new Promise((resolve) => setTimeout(() => resolve(undefined), NAME_WAIT_MS))]);
        if(!stillPlacing()) return;
        this.showCallDialog({ otherUser: known || { fullname: String(callee_id) }, callState: 'outgoing_ringing', direction: 'outgoing' });
        if(known === undefined) {
            const screen = this._callScreen;
            lookup.then((user) => {
                if(user?.fullname && this._callScreen === screen) {
                    this.updateDialogState({ name: user.fullname, avatarUrl: user.avatar_128 || user.avatar_512 || '' });
                }
            });
        }

        // No microphone, no call: said on the screen, before anyone's phone
        // rings. Asks for it if the app never has.
        const micProblem = await this.checkMicrophoneForCall?.();
        if(!stillPlacing()) return;
        if(micProblem) {
            this.callState = 'registered';
            this._outgoingAttempt = null;
            this.endCallDialog({ reason: micProblem }, 'sip');
            return;
        }

        // callState alone doesn't prove the transport is actually alive - a
        // zombie WSS socket (post-sleep NAT/interface loss) still reads as
        // 'registered', and sip.js swallows a dead-socket INVITE send with no
        // rejection at all (see node_modules/sip.js's InviteClientTransaction:
        // "Failed to send initial outgoing request." is only logged, never
        // thrown), so the call would otherwise just silently do nothing for
        // 32s (Timer B) with zero feedback. Run the same real probe the
        // heartbeat uses right before dialing, so a dead transport gets
        // caught and reconnected up front instead.
        try {
            await this.sendOptionsPing(HEARTBEAT_TIMEOUT);
        } catch (err) {
            console.warn('Pre-call transport check failed - reconnecting', err);
            if(!stillPlacing()) return;
            this.callState = 'registered';
            this._outgoingAttempt = null;
            this.handleTransportDisconnect(err);
            // "Couldn't connect" with Call again: by the time it is pressed
            // the reconnect has usually gone through.
            this.endCallDialog({ reason: 'failed' }, 'sip');
            return;
        }
        // Ended from the screen while the probe was out.
        if(!stillPlacing()) return;

        this.playRingback();
        // The initial INVITE is always audio-only, regardless of which button was
        // clicked - video (if requested) is added via a re-INVITE right after the
        // call connects instead. See handleSessionStateChange(): a video offer in
        // the very first INVITE runs ~6KB+ and reliably triggers a FreeSWITCH
        // ws.c bug where the auth-retry INVITE that immediately follows a 407
        // challenge sits unread in FreeSWITCH's socket buffer for ~30-40s (see
        // sdpModifiers.js's pruneVideoCodecs comment for the full mechanism).
        this.callVideo = video;
        const target = UserAgent.makeURI(`sip:${callee_id}@${this.sipDomain}`);
        this.session = new Inviter(this.userAgent, target, {
            sessionDescriptionHandlerOptions: { constraints: AUDIO_CONSTRAINTS },
            sessionDescriptionHandlerModifiers: [pruneVideoCodecs]
        });

        const session = this.session;
        this.session.stateChange.addListener((state) => this.handleSessionStateChange(state));

        try {
            await this.session.invite({
                requestDelegate: {
                    // 180 Ringing (or 183 with the ringback) - the call has
                    // reached a phone: "Calling…" becomes "Ringing…".
                    onProgress: (response) => {
                        const code = response?.message?.statusCode;
                        if(this.session === session && (code === 180 || code === 183) && this.callState === 'outgoing_ringing') {
                            this.updateDialogState({ phase: 'ringing' });
                        }
                    },
                    // Busy, no answer, not available... - kept for the
                    // teardown the Terminated state brings right after.
                    onReject: (response) => {
                        const code = response?.message?.statusCode;
                        traceCall('rejected', { statusCode: code });
                        if(this.session === session && !this._endReason) this._endReason = endReasonFromSipStatus(code);
                    },
                },
            });
        } catch (err) {
            console.error('Failed to start call', err);
            // Most often getUserMedia: no permission, no microphone, or one
            // another app holds.
            this.teardownCall({ reason: endReasonFromMediaError(err) || 'failed' });
        }
    },

    //********************* HANDLE INCOMING INVITE ********************************//
    // Wired as the UserAgent's delegate.onInvite - the only source of truth for
    // incoming calls (carries the live Invitation needed to answer). The
    // 'gudhub_sip' push event (sipEventsMixin) is advisory-only and must not
    // drive this state machine.
    // * invitation (SIP.Invitation)
    async handleIncomingInvite(invitation) {
        if(this.callState !== 'registered') {
            traceCall('inviteRejected', { callState: this.callState });
            invitation.reject({ statusCode: 486 }); // Busy Here
            return;
        }

        this.callState = 'incoming_ringing';
        this._endReason = null;
        this._lastCallee = null;
        this.isMuted = false;
        this.isCameraOff = false;
        // The caller gave up, or another of this user's devices answered
        // (a CANCEL with "Call completed elsewhere") - said on the screen.
        invitation.delegate = {
            ...(invitation.delegate || {}),
            onCancel: (cancel) => {
                let reason = 'missed';
                try {
                    const header = String(cancel?.request?.getHeader?.('Reason') || '');
                    if(/cause=200|completed elsewhere/i.test(header)) reason = 'answeredElsewhere';
                } catch (err) { /* plain "missed" */ }
                if(this.session === invitation) this._endReason = reason;
            },
        };
        // The offer's SDP naturally has no video m-line when the caller placed
        // an audio-only call (their SDH never added a video track) - reading
        // that directly avoids depending on a custom SIP header surviving the
        // relay through the SIP server, which a prior attempt at this showed
        // isn't reliable (audio calls stopped ringing at all).
        this.callVideo = /^m=video/m.test(invitation.body || '');
        this.session = invitation;
        this.session.stateChange.addListener((state) => this.handleSessionStateChange(state));

        const callerId = invitation.remoteIdentity.uri.user;
        const callerDisplayName = invitation.remoteIdentity.displayName;
        traceCall('invite', { callerId, native: Boolean(this.nativeCallActive) });

        // PSTN callers (inbound trunk calls) aren't GudHub users - a failed
        // lookup must not prevent the dialog from opening, or the call rings
        // with no way to answer it. Prefer the SIP display name (set
        // server-side from a connected_apps match against the caller's
        // number - see sip-gudhub-server's dialplan.js) over the raw number
        // when present.
        const otherUser = Promise.resolve()
            .then(() => gudhub.getUserById(String(callerId)))
            .catch((err) => {
                console.warn(`No GudHub user for caller "${callerId}", treating as external`, err);
                return null;
            })
            .then((user) => user || { fullname: callerDisplayName || String(callerId) });

        // In the mobile shells the OS already owns this moment: CallKit on
        // iOS and the full-screen call notification on Android were shown by
        // the push that woke the device, before this INVITE even arrived, and
        // both play their own ringtone. Ringing and opening a second dialog
        // on top of that gives the user two overlapping call screens and two
        // ringtones out of sync with each other.
        //
        // nativeCallActive is set by nativeCallsMixin when it reports a call
        // to the native layer, and cleared as soon as that call ends - so a
        // call arriving while the app is already open in the foreground (no
        // push involved) still takes the normal path below.
        //
        // The native screen is the UI until the user answers there; the answer
        // comes back through nativeCallsMixin, which opens the dialog and
        // accepts. Stored before the name lookup resolves, so an answer that is
        // already waiting is not held up by a network round trip.
        if(this.nativeCallActive) {
            this.pendingNativeInvitation = invitation;
            this.pendingNativeCaller = otherUser;
            return;
        }

        this.playRingtone();

        const user = await otherUser;
        // The caller may have hung up while the name was being looked up.
        if(this.session !== invitation) return;

        this.showCallDialog({ otherUser: user, direction: 'incoming' });
    },

    //********************* PLAY/STOP RINGTONE ********************************//
    // Alerts the callee of an incoming call; stopped as soon as the invitation
    // leaves 'incoming_ringing' for any reason (answered, declined, cancelled).
    playRingtone() {
        this._ringtoneAudio ??= new Audio(ringtoneUrl);
        this._ringtoneAudio.loop = true;
        this._ringtoneAudio.currentTime = 0;
        this._ringtoneAudio.play().catch((err) => console.warn('Ringtone playback failed', err));
    },

    stopRingtone() {
        if(!this._ringtoneAudio) return;
        this._ringtoneAudio.pause();
        this._ringtoneAudio.currentTime = 0;
    },

    //********************* PLAY/STOP RINGBACK ********************************//
    // Alerts the caller their own outgoing call is ringing. FreeSWITCH does send
    // a real ringback tone as early media (183 Session Progress), but sip.js's
    // Inviter never applies an early SDP answer when the initial INVITE itself
    // already carries an offer (the normal case here, per startOutgoingCall) -
    // confirmed in sip.js's own source (Inviter.onProgress,
    // SignalingState.HaveLocalOffer branch just returns without touching the
    // peer connection), so that early media never reaches the browser at all.
    // A local file sidesteps sip.js's early-media handling entirely, mirroring
    // playRingtone()'s same approach for the callee side. Stopped as soon as
    // the call leaves 'outgoing_ringing' for any reason (answered, cancelled,
    // rejected, failed).
    playRingback() {
        this._ringbackAudio ??= new Audio(ringbackUrl);
        this._ringbackAudio.loop = true;
        this._ringbackAudio.currentTime = 0;
        this._ringbackAudio.play().catch((err) => console.warn('Ringback playback failed', err));
    },

    stopRingback() {
        if(!this._ringbackAudio) return;
        this._ringbackAudio.pause();
        this._ringbackAudio.currentTime = 0;
    },

    //********************* ACCEPT INCOMING CALL ********************************//
    async acceptIncomingCall() {
        if(!this.session || this.callState !== 'incoming_ringing') return;

        // sip.js's own session.state moves out of Initial the instant
        // accept() starts, well before our callState catches up (that only
        // flips once Established, via handleSessionStateChange). A second
        // accept() call landing in that gap - a double-tap on Accept, more
        // likely with a landline call's slower setup - hits sip.js's own
        // guard and throws "Invalid session state Establishing". Checking
        // the session's real state here closes that gap.
        if(this.session.state !== SessionState.Initial) return;

        const invitation = this.session;
        this.updateDialogState({ phase: 'connecting' });

        // Answering without a microphone would connect a call nobody can
        // hear. Refused with 480, not 486: the user's other devices ring on.
        const micProblem = await this.checkMicrophoneForCall?.();
        if(this.session !== invitation || invitation.state !== SessionState.Initial) return;
        if(micProblem) {
            this._endReason = micProblem;
            invitation.reject({ statusCode: 480 });
            return;
        }

        try {
            // Incoming offers are always audio-only now (callers never put video in
            // the first INVITE - see startOutgoingCall) - accept audio-only here too,
            // and prime the session to answer a video-upgrade re-INVITE once
            // Established (see handleSessionStateChange).
            await this.session.accept({
                sessionDescriptionHandlerOptions: { constraints: AUDIO_CONSTRAINTS },
                sessionDescriptionHandlerModifiers: [pruneVideoCodecs]
            });
        } catch (err) {
            console.error('Failed to accept call', err);
            traceCall('acceptFailed', { error: err?.message });
            this.teardownCall({ reason: endReasonFromMediaError(err) || 'failed' });
        }
    },

    //********************* REJECT INCOMING CALL ********************************//
    rejectIncomingCall() {
        if(!this.session || this.callState !== 'incoming_ringing') return;
        // 486 (Busy Here), not SIP.js's default 480 - the server's dialplan
        // matches this exact cause via fail_on_single_reject=USER_BUSY to
        // cancel this user's other still-ringing tabs (see dialplan.js's
        // bridgeWithVoicemailFallback).
        // The user's own no: the screen closes at once, without "Call ended".
        this._endReason ??= 'declinedHere';
        this.session.reject({ statusCode: 486 });
    },

    //********************* CANCEL OUTGOING CALL ********************************//
    cancelOutgoingCall() {
        traceCall('cancelOutgoing', { callState: this.callState, sessionState: this.session?.state });

        // Ended before the INVITE went out (the screen is up while the
        // microphone and the connection are checked): nothing to cancel on
        // the wire - startOutgoingCall sees the attempt is gone and stops.
        if(!this.session && this.callState === 'outgoing_ringing') {
            this._outgoingAttempt = null;
            this.callState = 'registered';
            this.stopRingback();
            this.endCallDialog({ reason: 'cancelled' }, 'sip');
            return;
        }
        if(!this.session || this.callState !== 'outgoing_ringing') return;
        this._endReason ??= 'cancelled';

        // A double-click, or a race with the far end already failing the
        // call, leaves the session Terminating/Terminated while callState is
        // still 'outgoing_ringing' - cancel() then throws "Invalid session
        // state". Force the local teardown instead so the dialog can't wedge.
        if(this.session.state === SessionState.Terminating || this.session.state === SessionState.Terminated) {
            this.teardownCall();
            return;
        }
        this.session.cancel().catch((err) => {
            console.warn('CANCEL failed, forcing teardown', err);
            this.teardownCall();
        });
    },

    //********************* HANG UP CALL ********************************//
    hangupCall() {
        if(this.nativeCallView) return this.hangupNativeCall();
        if(!this.session) return;

        // Same race as cancelOutgoingCall - never throw on an already-dying
        // session, just make sure the UI resets.
        if(this.session.state === SessionState.Terminating || this.session.state === SessionState.Terminated) {
            this.teardownCall();
            return;
        }

        if(this.session.state === SessionState.Established) {
            this._endReason ??= 'ended';
            this.session.bye();
        } else if(this.session instanceof Inviter) {
            this._endReason ??= 'cancelled';
            this.session.cancel();
        } else {
            this._endReason ??= 'declinedHere';
            this.session.reject();
        }
    },

    //********************* TOGGLE MUTE ********************************//
    // Audio only - a plain `if(sender.track)` here previously disabled every
    // sender regardless of kind, so muting also blacked out the local video
    // feed on video calls as an unintended side effect.
    //
    // Allowed from the moment the screen is up: muted while it still rings,
    // the call connects muted (applyMute at Established).
    toggleMute() {
        if(this.nativeCallView) return this.toggleNativeMute();
        if(!this.session && this.callState !== 'outgoing_ringing') return;

        this.isMuted = !this.isMuted;
        this.applyMute();
        this.updateDialogState({ isMuted: this.isMuted });
    },

    applyMute() {
        const peerConnection = this.session?.sessionDescriptionHandler?.peerConnection;
        peerConnection?.getSenders().forEach((sender) => {
            if(sender.track?.kind === 'audio') sender.track.enabled = !this.isMuted;
        });
    },

    //********************* TOGGLE CAMERA ********************************//
    // Video only - mirrors toggleMute() exactly, filtered to the video
    // sender instead of the audio one. On a call that hasn't negotiated video
    // yet (no video sender), the first press instead adds it via the same
    // re-INVITE upgrade path upgradeToVideo() uses automatically for a call
    // placed with the camera button - the pruned SDP keeps this the same
    // size/shape as that already-proven-safe upgrade, so it's not at risk of
    // the ws.c wedge bug documented in sdpModifiers.js.
    //
    // Gated on !this.callVideo too, not just !hasVideoSender: if both parties
    // press this within the same short window, each side independently
    // upgrading races two uncoordinated re-INVITEs against each other on
    // FreeSWITCH's two separate bridge legs - confirmed live this collapses
    // the whole call's ICE/DTLS into a failed state, not just a redundant
    // no-op. Once either side's video is confirmed (self-initiated or
    // detected from the peer via watchForVideoTrack()), a later press is
    // just the normal mute/unmute toggle - the far end's auto-accept already
    // turns their own camera on with no click needed on their part.
    async toggleCamera() {
        // Calls in the iOS native engine are audio-only for now.
        if(this.nativeCallView) return;
        if(!this.session?.sessionDescriptionHandler || this._startingVideo) return;

        const peerConnection = this.session.sessionDescriptionHandler.peerConnection;
        const hasVideoSender = peerConnection.getSenders().some((sender) => sender.track?.kind === 'video');

        if(!hasVideoSender && !this.callVideo) {
            this._startingVideo = true;
            const upgraded = await this.upgradeToVideo();
            this._startingVideo = false;
            if(!upgraded) return;

            this.callVideo = true;
            this.isCameraOff = false;
            this.updateDialogState({ callVideo: true, isCameraOff: false });
            return;
        }

        this.isCameraOff = !this.isCameraOff;
        peerConnection.getSenders().forEach((sender) => {
            if(sender.track?.kind === 'video') sender.track.enabled = !this.isCameraOff;
        });

        this.updateDialogState({ isCameraOff: this.isCameraOff });
    },

    //********************* HANDLE SESSION STATE CHANGE ********************************//
    // Shared stateChange listener for both Inviter (outgoing) and Invitation
    // (incoming) sessions - both are SIP.Session subclasses.
    handleSessionStateChange(state) {
        traceCall('session', { state, callState: this.callState });

        if(state === SessionState.Established) {
            this.stopRingtone();
            this.stopRingback();
            this.callState = 'in_call';
            this.isCameraOff = false;
            // A mute pressed while it rang carries into the call.
            this.applyMute();
            this.attachMediaStreams();
            this.watchForVideoTrack();
            this.watchConnection();
            this.updateDialogState({ callState: this.callState, isMuted: this.isMuted, isCameraOff: false });

            // sip.js has no accept/reject hook for an in-dialog re-INVITE - it
            // auto-answers using whatever sessionDescriptionHandlerOptions/
            // ModifiersReInvite are already set on the session the moment one
            // arrives (see Session.onInviteRequest). Prime both unconditionally -
            // either party can now trigger a video upgrade mid-call (see
            // toggleCamera()), not just the original caller, so whichever side
            // *receives* the re-INVITE needs to be ready to answer it with video
            // instead of silently staying audio-only.
            this.session.sessionDescriptionHandlerOptionsReInvite = { constraints: VIDEO_CONSTRAINTS };
            this.session.sessionDescriptionHandlerModifiersReInvite = [pruneVideoCodecs, debundleVideo];
            // The re-INVITE itself is auto-accepted before we ever see it -
            // this delegate only fires as an FYI afterwards, which is exactly
            // when we need to re-run attachMediaStreams() to pick up our own
            // newly-added local video sender (see its comment on why the local
            // half doesn't update itself).
            this.session.delegate = { ...(this.session.delegate || {}), onInvite: () => this.attachMediaStreams() };

            if(this.session instanceof Inviter && this.callVideo) {
                this.upgradeToVideo();
            }
        } else if(state === SessionState.Terminated) {
            this.teardownCall();
        }
    },

    //********************* UPGRADE TO VIDEO ********************************//
    // Fired right after Established for a caller-initiated video call - the
    // initial INVITE is always audio-only (see startOutgoingCall), so an actual
    // video call adds its video m-line here via a re-INVITE instead. Failure
    // just logs and leaves the call running as audio-only rather than tearing
    // down an otherwise-working call.
    async upgradeToVideo() {
        try {
            await this.session.invite({
                sessionDescriptionHandlerOptions: { constraints: VIDEO_CONSTRAINTS },
                sessionDescriptionHandlerModifiers: [pruneVideoCodecs, debundleVideo]
            });
            // Unlike the remote half, attachMediaStreams()'s local-video snapshot
            // doesn't update itself when a new sender is added later - re-run it.
            this.attachMediaStreams();
            return true;
        } catch (err) {
            console.error('Failed to upgrade call to video', err);
            return false;
        }
    },

    //********************* WATCH FOR VIDEO TRACK ********************************//
    // The recipient's callVideo (unlike the caller's, which is set directly
    // from the phone/camera button clicked) is only a guess parsed from the
    // raw incoming INVITE's SDP text before the call even connects, and
    // isn't reliable in practice. A one-shot check of
    // peerConnection.remoteDescription right at Established isn't reliable
    // either - attachMediaStreams()'s remoteMediaStream comment above notes
    // tracks (especially on the answering side) are known to arrive *after*
    // Established. Listening for the peer connection's own 'track' event
    // catches a video track whenever it actually shows up, instead of
    // guessing upfront - fixing the camera/fullscreen buttons (gated on
    // callVideo) staying hidden for recipients on real video calls.
    watchForVideoTrack() {
        const peerConnection = this.session?.sessionDescriptionHandler?.peerConnection;
        if(!peerConnection) return;

        const confirmCallVideo = () => {
            if(this.callVideo) return;
            this.callVideo = true;
            this.updateDialogState({ callVideo: true });
            // Re-bind <video>.srcObject rather than trusting the remote
            // stream's own 'addtrack' listener (see attachMediaStreams()) to
            // pick this up on its own - confirmed live that a video track
            // added to an already-srcObject-bound stream can silently never
            // render (audio kept working, black video element) until the
            // element's srcObject is reassigned.
            this.attachMediaStreams();
        };

        if(peerConnection.getReceivers().some((receiver) => receiver.track?.kind === 'video')) {
            confirmCallVideo();
        }

        peerConnection.addEventListener('track', (event) => {
            if(event.track.kind === 'video') confirmCallVideo();
        });
    },

    //********************* WATCH CONNECTION ********************************//
    // The network dropping mid-call: "Reconnecting…" while ICE tries to
    // recover (a Wi-Fi/cellular hand-over usually does within seconds), back
    // to the timer when it has, and a "Couldn't connect" ending when it gave
    // up - never a timer silently counting on over a dead line.
    watchConnection() {
        const session = this.session;
        const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
        if(!peerConnection || peerConnection._ghConnectionWatched) return;
        peerConnection._ghConnectionWatched = true;

        peerConnection.addEventListener('iceconnectionstatechange', () => {
            if(this.session !== session || this.callState !== 'in_call') return;
            const state = peerConnection.iceConnectionState;
            traceCall('ice', { state });
            if(state === 'disconnected') {
                this.updateDialogState({ phase: 'reconnecting' });
            } else if(state === 'connected' || state === 'completed') {
                this.updateDialogState({ phase: 'active' });
            } else if(state === 'failed') {
                this._endReason = 'failed';
                this.hangupCall();
            }
        });
    },

    //********************* TEARDOWN CALL ********************************//
    // Stops local media tracks, closes the dialog, and resets back to 'registered'
    // (or 'idle' if the SIP UserAgent itself isn't registered, e.g. mid-teardown).
    // * end ({ reason }) — how the call ended, for the call screen; by
    //   default whatever the session's own events recorded (_endReason).
    teardownCall(end) {
        const reason = end?.reason || this._endReason || 'ended';
        traceCall('teardown', { callState: this.callState, reason });
        this._endReason = null;
        this._outgoingAttempt = null;

        this.stopRingtone();
        this.stopRingback();

        const peerConnection = this.session?.sessionDescriptionHandler?.peerConnection;
        peerConnection?.getSenders().forEach((sender) => sender.track?.stop());
        this._localStream?.getTracks().forEach((track) => track.stop());
        this._localStream = null;

        this.session = null;
        this.isMuted = false;
        this.isCameraOff = false;
        // In the iOS shell this agent never registers (nativeEngineMixin.js) -
        // connected is ready for the next call there.
        this.callState = this.registerer || (this.nativeEngine && this.userAgent) ? 'registered' : 'idle';
        // How it ended stays on the screen for a moment ("Call ended 2:14",
        // "Busy" with Call again); a call the user declined just closes.
        if(reason === 'declinedHere') this.closeCallDialog('sip');
        else this.endCallDialog({ reason }, 'sip');

        // Every path out of a call passes through here, which makes it the only
        // safe place to drop the native call state. Relying on the plugin's own
        // callEnded event was not enough: a call torn down purely on the SIP
        // side - a remote hangup, a 486, the caller giving up - never produces
        // one, leaving nativeCallActive stuck true. handleIncomingInvite then
        // treats every later call as "the OS is showing this one" and quietly
        // shows no UI at all, which is why calls worked exactly once per app
        // launch.
        this.clearNativeCallState?.();
    },
};
